// API helpers for Manna Bridal store
// All communication goes through the Laravel backend.

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://mannabridal.onrender.com/api';

// ── Keep Render backend awake (ping every 10 minutes) ──────────────────────
const PING_URL = (process.env.REACT_APP_API_URL || 'https://mannabridal.onrender.com/api') + '/ping';
const ping = () => fetch(PING_URL, { method: 'GET' }).catch(() => {});
ping(); // ping immediately on page load
setInterval(ping, 10 * 60 * 1000); // then every 10 minutes

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 seconds timeout to allow for SMTP email sending
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  }
});

// Attach auth token to every request automatically
api.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle global 401 errors
let isAuthExpiredEventDispatched = false;
api.interceptors.response.use((response) => response, (error) => {
  if (error.response && error.response.status === 401 && error.config && !error.config.url.includes('/auth/login')) {
    if (!isAuthExpiredEventDispatched) {
      isAuthExpiredEventDispatched = true;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.dispatchEvent(new Event('auth-expired'));
      setTimeout(() => { isAuthExpiredEventDispatched = false; }, 2000);
    }
  }
  return Promise.reject(error);
});

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const login = async (email, password) => {
  const res = await api.post('/auth/login', { email, password });
  return res.data;
};

export const sendRegistrationOtp = async (name, email, password) => {
  const res = await api.post('/auth/send-registration-otp', { name, email, password });
  return res.data;
};

export const register = async (email, otp) => {
  const res = await api.post('/auth/register', { email, otp });
  return res.data;
};

export const logout = async () => {
  try { await api.post('/auth/logout'); } catch (_) {}
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
};

export const fetchCurrentUser = async () => {
  try {
    const res = await api.get('/auth/me');
    return res.data;
  } catch (err) {
    return null;
  }
};

export const updateProfile = async (profileData) => {
  const res = await api.put('/auth/profile', profileData);
  return res.data;
};

// ─── Storefront Fetchers ──────────────────────────────────────────────────────

const formatImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/storage/')) {
    // Strip /api from the end to get the base URL
    const baseUrl = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL.replace('/api', '');
    return `${baseUrl}${url}`;
  }
  
  // Auto-optimize Cloudinary URLs (serve WebP/AVIF and compress automatically)
  if (url.includes('res.cloudinary.com') && !url.includes('f_auto,q_auto')) {
    return url.replace('/upload/', '/upload/f_auto,q_auto/');
  }
  
  return url;
};

export const fetchProducts = async () => {
  const res = await api.get('/products');
  const products = res.data.map(p => {
    let imgs = [];
    if (Array.isArray(p.images)) {
      imgs = p.images;
    } else if (typeof p.images === 'string') {
      try {
        imgs = JSON.parse(p.images);
      } catch (_) {}
    }
    const formattedImages = imgs.map(img => formatImageUrl(img));
    const coverImage = formattedImages.find(img => !img.match(/\.(mp4|webm|ogg)$/i)) || null;

    // Normalise sizes/colors — may come back as array or JSON string
    const parseArray = (val) => {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') { try { return JSON.parse(val); } catch (_) {} }
      return [];
    };

    return {
      ...p,
      images: formattedImages,
      image: coverImage,
      sizes: parseArray(p.sizes),
      colors: parseArray(p.colors),
      price: Number(p.price || 0),
      original_price: p.original_price ? Number(p.original_price) : null,
      stock: Number(p.stock || 0),
      description: p.description || '',
      category: p.category?.name || (typeof p.category === 'string' ? p.category : ''),
    };
  });

  // Fisher-Yates shuffle — scatter products so newest isn't always first
  for (let i = products.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [products[i], products[j]] = [products[j], products[i]];
  }

  return products;
};

export const fetchBanners = async () => {
  const res = await api.get('/banners');
  return res.data.map(b => ({
    ...b,
    image: formatImageUrl(b.image_url),
    start: b.start_date,
    end: b.end_date,
  }));
};

export const fetchHeroSlides = async () => {
  const res = await api.get('/hero-slides');
  return res.data.map(s => ({
    ...s,
    image: formatImageUrl(s.image_url),
    cta: s.cta_text,
    dark: s.is_dark,
  }));
};

export const fetchTheme = async () => {
  const res = await api.get('/settings');
  try {
    return res.data.theme_colors ? JSON.parse(res.data.theme_colors) : null;
  } catch (_) {
    return null;
  }
};

export const fetchSettings = async () => {
  const res = await api.get('/settings');
  return res.data;
};

export const fetchCategories = async () => {
  const res = await api.get('/categories');
  return res.data.map(cat => ({
    ...cat,
    image_url: formatImageUrl(cat.image_url)
  }));
};

export const fetchWishlist = async () => {
  const res = await api.get('/wishlist');
  return res.data;
};

export const toggleWishlist = async (productId) => {
  const res = await api.post('/wishlist/toggle', { product_id: productId });
  return res.data;
};

// ─── Customer Orders ──────────────────────────────────────────────────────────

export const placeOrder = async (items, shippingAddress, billingAddress, customerPhone, state) => {
  const res = await api.post('/orders', {
    items,
    shipping_address: shippingAddress,
    billing_address: billingAddress,
    customer_phone: customerPhone,
    state
  });
  return res.data;
};

export const fetchOrders = async () => {
  const res = await api.get('/orders');
  return res.data;
};

export const confirmDelivery = async (orderId) => {
  const res = await api.post(`/orders/${orderId}/confirm-delivery`);
  return res.data;
};

export const cancelOrder = async (orderId) => {
  const res = await api.post(`/orders/${orderId}/cancel`);
  return res.data;
};

export const rateOrderProducts = async (orderId, ratings) => {
  const res = await api.post(`/orders/${orderId}/rate`, { ratings });
  return res.data;
};

// ─── Payments ─────────────────────────────────────────────────────────────────

export const initializePayment = async (orderId) => {
  const res = await api.post(`/payments/${orderId}/initialize`);
  return res.data;
};

export const verifyPayment = async (reference) => {
  const res = await api.get(`/payments/${reference}/verify`);
  return res.data;
};

export const verifyOrderPayment = async (orderId) => {
  const res = await api.post(`/orders/${orderId}/verify-payment`);
  return res.data;
};

// ─── Admin: Products ──────────────────────────────────────────────────────────

export const fetchAdminProducts = async () => {
  const res = await api.get('/admin/products');
  return res.data.map(p => {
    let imgs = [];
    if (Array.isArray(p.images)) {
      imgs = p.images;
    } else if (typeof p.images === 'string') {
      try { imgs = JSON.parse(p.images); } catch (_) {}
    }
    const formattedImages = imgs.map(img => formatImageUrl(img));
    const coverImage = formattedImages.find(img => !img.match(/\.(mp4|webm|ogg)$/i)) || null;
    const parseArray = (val) => {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') { try { return JSON.parse(val); } catch (_) {} }
      return [];
    };
    return {
      ...p,
      images: formattedImages,
      image: coverImage,
      sizes: parseArray(p.sizes),
      colors: parseArray(p.colors),
      price: Number(p.price || 0),
      original_price: p.original_price ? Number(p.original_price) : null,
      stock: Number(p.stock || 0),
      description: p.description || '',
      category: p.category?.name || (typeof p.category === 'string' ? p.category : ''),
    };
  });
};

export const saveProduct = async (productData, id = null) => {
  let imageUrls = [];

  if (productData.mediaList && Array.isArray(productData.mediaList)) {
    for (const item of productData.mediaList) {
      if (item instanceof File) {
        imageUrls.push(await uploadImageToCloudinary(item));
      } else if (typeof item === 'string') {
        imageUrls.push(item);
      } else if (item && item.url) {
        imageUrls.push(item.url);
      }
    }
  } else {
    if (Array.isArray(productData.images)) {
      imageUrls = [...productData.images];
    }
    if (productData.imageFiles && productData.imageFiles.length > 0) {
      const uploadPromises = Array.from(productData.imageFiles).map(file => uploadImageToCloudinary(file));
      const newImageUrls = await Promise.all(uploadPromises);
      imageUrls = [...imageUrls, ...newImageUrls];
    }
  }

  const formData = new FormData();

  Object.keys(productData).forEach(key => {
    if (['imageFiles', 'images', 'existing_images', 'mediaList'].includes(key)) return;
    const val = productData[key];
    if (key === 'is_free_shipping') {
      // Send as 1/0 so Laravel's in_array check works correctly
      formData.append(key, val ? '1' : '0');
    } else if (Array.isArray(val)) {
      formData.append(key, JSON.stringify(val));
    } else if (val !== null && val !== undefined) {
      formData.append(key, val);
    }
  });

  // Append all collected image URLs
  imageUrls.forEach(url => formData.append('existing_images[]', url));

  if (id) {
    formData.append('_method', 'PUT');
    const res = await api.post(`/admin/products/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  } else {
    const res = await api.post('/admin/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }
};

export const deleteProduct = async (id) => {
  await api.delete(`/admin/products/${id}`);
};

// ─── Admin: Shared Helpers ────────────────────────────────────────────────────

/**
 * Compress an image file before uploading.
 * Resizes to max 1200px on the longest side and converts to JPEG at 82% quality.
 * Typically reduces a 5MB photo to under 300KB — 10-20x faster upload.
 * Videos and GIFs are returned unchanged.
 */
const compressImage = (file, maxPx = 1200, quality = 0.82) => {
  return new Promise((resolve) => {
    // Skip non-image or already-small files (< 300KB) — not worth compressing
    if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.size < 300 * 1024) {
      return resolve(file);
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(maxPx / img.width, maxPx / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width  = Math.round(img.width  * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file),
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

/**
 * Upload an image file directly to Cloudinary from the browser.
 * Gets a signed token from our backend, then POSTs the file straight to
 * Cloudinary's CDN — no Render timeout risk at all.
 */
export const uploadImageToCloudinary = async (file) => {
  const compressed = await compressImage(file);
  const sigRes = await api.get('/admin/cloudinary-signature');
  const { api_key, cloud_name, timestamp, signature, folder } = sigRes.data;

  const cloudForm = new FormData();
  cloudForm.append('file', compressed);
  cloudForm.append('api_key', api_key);
  cloudForm.append('timestamp', String(timestamp));
  cloudForm.append('signature', signature);
  cloudForm.append('folder', folder);

  const cloudRes = await fetch(
    `https://api.cloudinary.com/v1_1/${cloud_name}/auto/upload`,
    { method: 'POST', body: cloudForm }
  );

  if (!cloudRes.ok) {
    const err = await cloudRes.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Cloudinary upload failed');
  }

  const cloudData = await cloudRes.json();
  return cloudData.secure_url;
};

// Upload image for customer inquiries — uses the customer-accessible signature endpoint
export const uploadInquiryImageToCloudinary = async (file) => {
  const compressed = await compressImage(file, 800, 0.80);
  const sigRes = await api.get('/customer/cloudinary-signature');
  const { api_key, cloud_name, timestamp, signature, folder } = sigRes.data;

  const cloudForm = new FormData();
  cloudForm.append('file', compressed);
  cloudForm.append('api_key', api_key);
  cloudForm.append('timestamp', String(timestamp));
  cloudForm.append('signature', signature);
  cloudForm.append('folder', folder);

  const cloudRes = await fetch(
    `https://api.cloudinary.com/v1_1/${cloud_name}/auto/upload`,
    { method: 'POST', body: cloudForm }
  );

  if (!cloudRes.ok) {
    const err = await cloudRes.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Cloudinary upload failed');
  }

  const cloudData = await cloudRes.json();
  return cloudData.secure_url;
};

// ─── Admin: Categories ────────────────────────────────────────────────────────

export const saveCategory = async (catData, id = null) => {
  // Preserve existing image: `imageUrl` for new uploads, `image_url` when editing
  let imageUrl = catData.imageUrl || catData.image_url || '';
  if (catData.imageFile instanceof File) {
    imageUrl = await uploadImageToCloudinary(catData.imageFile);
  }

  const payload = {
    name: catData.name,
    image_url: imageUrl,
  };

  if (id) {
    const res = await api.put(`/admin/categories/${id}`, payload);
    return res.data;
  } else {
    const res = await api.post('/admin/categories', payload);
    return res.data;
  }
};

export const deleteCategory = async (id) => {
  await api.delete(`/admin/categories/${id}`);
};

// ─── Admin: Banners ───────────────────────────────────────────────────────────

export const saveBanner = async (bannerData, id = null) => {
  // Preserve existing image: admin fetcher returns `image`, new uploads use `imageUrl`
  let imageUrl = bannerData.imageUrl || bannerData.image || bannerData.image_url || '';
  if (bannerData.imageFile instanceof File) {
    imageUrl = await uploadImageToCloudinary(bannerData.imageFile);
  }

  const payload = {
    title:     bannerData.title || '',
    image_url: imageUrl,
  };
  if (bannerData.subtitle !== undefined) payload.subtitle = bannerData.subtitle || null;
  if (bannerData.status)   payload.status   = bannerData.status;
  if (bannerData.start)    payload.start    = bannerData.start;
  if (bannerData.end)      payload.end      = bannerData.end;
  if (bannerData.cta !== undefined) payload.cta = bannerData.cta || null;

  if (id) {
    const res = await api.put(`/admin/banners/${id}`, payload);
    return res.data;
  } else {
    const res = await api.post('/admin/banners', payload);
    return res.data;
  }
};

export const deleteBanner = async (id) => {
  await api.delete(`/admin/banners/${id}`);
};

export const fetchAdminBanners = async () => {
  const res = await api.get('/admin/banners');
  return res.data.map(b => ({
    ...b,
    image: formatImageUrl(b.image_url) || null,
    start: b.start_date,
    end: b.end_date,
  }));
};

// ─── Admin: Hero Slides ───────────────────────────────────────────────────────

export const saveHeroSlide = async (slideData, id = null) => {
  let imageUrl = slideData.image || slideData.image_url || slideData.imageUrl || '';
  if (slideData.imageFile instanceof File) {
    imageUrl = await uploadImageToCloudinary(slideData.imageFile);
  }

  const payload = {
    title:     slideData.title || ('Slide ' + Date.now()),
    dark:      slideData.dark ? '1' : '0',
    image_url: imageUrl,
  };
  if (slideData.subtitle) payload.subtitle = slideData.subtitle;
  if (slideData.badge)    payload.badge    = slideData.badge;
  if (slideData.cta)      payload.cta      = slideData.cta;

  if (id) {
    const res = await api.put(`/admin/hero-slides/${id}`, payload);
    return res.data;
  } else {
    const res = await api.post('/admin/hero-slides', payload);
    return res.data;
  }
};

export const deleteHeroSlide = async (id) => {
  await api.delete(`/admin/hero-slides/${id}`);
};

export const fetchAdminHeroSlides = async () => {
  const res = await api.get('/admin/hero-slides');
  return res.data.map(s => ({
    ...s,
    image: formatImageUrl(s.image_url) || null,
  }));
};

// ─── Admin: Theme ─────────────────────────────────────────────────────────────

export const updateTheme = async (theme) => {
  const res = await api.put('/admin/settings/theme', { theme_colors: JSON.stringify(theme) });
  return res.data;
};

export const updateShippingSettings = async (settings) => {
  const res = await api.put('/admin/settings/shipping', settings);
  return res.data;
};

// ─── Admin: Notifications ─────────────────────────────────────────────────────

export const fetchAdminNotifications = async () => {
  const res = await api.get('/admin/notifications');
  return res.data;
};

export const markAdminNotificationsRead = async () => {
  const res = await api.post('/admin/notifications/mark-read');
  return res.data;
};

// ─── Admin: Orders ────────────────────────────────────────────────────────────

export const fetchAdminOrders = async (page = 1, perPage = 500) => {
  const res = await api.get('/admin/orders', { params: { page, per_page: perPage } });
  // Backend now returns { data: [...], meta: {...} }; fall back to plain array for safety
  return Array.isArray(res.data) ? res.data : res.data.data ?? [];
};


export const updateOrderStatus = async (id, data) => {
  const payload = typeof data === 'string' ? { status: data } : data;
  const res = await api.put(`/admin/orders/${id}`, payload);
  return res.data;
};

// ─── Admin: Users ─────────────────────────────────────────────────────────────

export const fetchAdminUsers = async () => {
  const res = await api.get('/admin/users');
  return res.data;
};

export const saveAdminUser = async (userData) => {
  const res = await api.post('/admin/users', userData);
  return res.data;
};

export const deleteAdminUser = async (id) => {
  await api.delete(`/admin/users/${id}`);
};

export const fetchAdminCustomers = async () => {
  const res = await api.get('/admin/customers');
  return res.data;
};

export const purgeAllOrders = async () => {
  const res = await api.delete('/admin/orders');
  return res.data;
};

// ─── Auth: Forgot / Reset / Change Password ───────────────────────────────────

export const forgotPassword = async (email) => {
  const res = await api.post('/auth/forgot-password', { email });
  return res.data;
};

export const verifyOtp = async (email, otp) => {
  const res = await api.post('/auth/verify-otp', { email, otp });
  return res.data;
};

export const resetPassword = async (email, otp, password, passwordConfirmation) => {
  const res = await api.post('/auth/reset-password', {
    email,
    otp,
    password,
    password_confirmation: passwordConfirmation,
  });
  return res.data;
};

export const changePassword = async (password, passwordConfirmation) => {
  const res = await api.post('/auth/change-password', {
    password,
    password_confirmation: passwordConfirmation,
  });
  return res.data;
};

// ─── Admin: Root Actions ────────────────────────────────────────────────────────

export const rootListUsers = async () => (await api.get('/root/users')).data;
export const rootAssignRole = async (userId, role) => (await api.put(`/root/users/${userId}/role`, { role })).data;
export const rootGetPaymentConfig = async (token) => (await api.get('/root/payment-config', { headers: { 'X-Payment-Auth-Token': token } })).data;
export const rootUpdatePaymentConfig = async (config, token) => (await api.post('/root/payment-config', config, { headers: { 'X-Payment-Auth-Token': token } })).data;
export const rootRequestPaymentAuth = async (password) => (await api.post('/root/payment-auth/request', { password })).data;
export const rootVerifyPaymentAuth = async (otp) => (await api.post('/root/payment-auth/verify', { otp })).data;
export const rootGetLogs = async () => (await api.get('/root/logs')).data;
export const rootGetActivityLogs = async () => (await api.get('/root/activity-logs')).data;
export const rootGetFinanceDashboard = async () => (await api.get('/root/finance-dashboard')).data;
export const rootRequestActionAuth = async (password, action_name) => (await api.post('/root/action-auth/request', { password, action_name })).data;
export const rootPurgeData = async (type, otp) => (await api.post('/root/purge', { type, otp })).data;
export const rootPurgeUsers = async (otp) => (await api.post('/root/purge-users', { otp })).data;
export const rootPurgeDatabase = async (otp) => (await api.post('/root/purge-data', { otp })).data;
export const rootBroadcast = async (subject, message) => (await api.post('/root/broadcast', { subject, message })).data;
export const rootReverifyPayments = async () => (await api.post('/root/reverify-payments')).data;
export const rootDownloadReport = () => {
  const token = localStorage.getItem('auth_token');
  const url = `${API_URL}/root/report`;
  const a = document.createElement('a');
  a.href = url;
  fetch(url, { headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' } })
    .then(r => r.blob())
    .then(blob => {
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = `manna-bridal-report-${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(objUrl);
    });
};

export const rootDownloadPdf = (endpoint, filename) => {
  const token = localStorage.getItem('auth_token');
  const url = `${API_URL}${endpoint}`;
  return fetch(url, { headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' } })
    .then(r => {
      if (!r.ok) throw new Error('Failed to generate PDF');
      return r.blob();
    })
    .then(blob => {
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    });
};

export const rootDownloadFinancePdf = () =>
  rootDownloadPdf('/root/reports/finance', `finance-report-${new Date().toISOString().slice(0,10)}.pdf`);

export const rootDownloadActivityPdf = () =>
  rootDownloadPdf('/root/reports/activity', `activity-report-${new Date().toISOString().slice(0,10)}.pdf`);

export const rootDownloadLogsPdf = () =>
  rootDownloadPdf('/root/reports/logs', `system-logs-${new Date().toISOString().slice(0,10)}.pdf`);

// ─── Customer Chat Actions ────────────────────────────────────────────────────
export const fetchChatMessages = async () => {
  let sId = localStorage.getItem('mb_session_id');
  if (!sId) {
    sId = 's_' + Math.random().toString(36).substr(2, 9) + Date.now();
    localStorage.setItem('mb_session_id', sId);
  }
  const res = await api.get(`/chat?session_id=${sId}`);
  return res.data;
};

export const sendChatMessage = async (message) => {
  const sId = localStorage.getItem('mb_session_id');
  const res = await api.post('/chat', { message, session_id: sId });
  return res.data;
};

// ─── Admin Chat Actions ───────────────────────────────────────────────────────
export const adminFetchChats = async () => {
  const res = await api.get('/admin/chat');
  return res.data;
};

export const adminFetchChatThread = async (userId, sessionId) => {
  const params = new URLSearchParams();
  if (userId) params.append('user_id', userId);
  if (sessionId) params.append('session_id', sessionId);
  const res = await api.get(`/admin/chat/thread?${params.toString()}`);
  return res.data;
};

export const adminSendChatReply = async (message, userId, sessionId) => {
  const res = await api.post('/admin/chat/reply', {
    message,
    user_id: userId,
    session_id: sessionId
  });
  return res.data;
};

export const rootGetUiSections = async () => {
  const res = await api.get('/root/ui-sections');
  return res.data;
};

export const rootUpdateUiSections = async (sections) => {
  const res = await api.post('/root/ui-sections', { sections });
  return res.data;
};

export const sendSupportMessage = async (message) => {
  const res = await api.post('/support/message', { message });
  return res.data;
};

// ─── Backup & Restore (Root only) ─────────────────────────────────────────────
export const rootExportDatabase = async () => {
  // Return the raw response so we can handle the blob/download in the UI
  const res = await api.get('/root/backup/export', { responseType: 'blob' });
  return res;
};

export const rootImportDatabase = async (file) => {
  const formData = new FormData();
  formData.append('backup_file', file);
  const res = await api.post('/root/backup/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
};
